/**
 * GC3H Canteen - API Configuration
 * 
 * CRITICAL DEPLOYMENT RULES:
 * 1. In production, ALWAYS use same-origin (ignores env var if on valid host)
 * 2. Blocked URL patterns are rejected
 * 3. Cross-project calls are prevented
 */

// BLOCKED patterns - these should NEVER be used
const BLOCKED_PATTERNS = [
  'menu-scheduler',
  'localhost:8001',
  '127.0.0.1:8001',
];

// Check if URL contains blocked pattern
const isBlockedUrl = (url) => {
  if (!url) return false;
  return BLOCKED_PATTERNS.some(pattern => url.includes(pattern));
};

// Detect current environment
const detectEnvironment = () => {
  if (typeof window === 'undefined') return 'build';
  
  const host = window.location.hostname;
  
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'development';
  }
  if (host.includes('.preview.emergentagent.com')) {
    return 'staging';
  }
  if (host.includes('.emergent.host')) {
    return 'production';
  }
  // Custom domain for GC3H Online Canteen
  if (host.includes('gc3honlinecanteen.co.za')) {
    return 'production';
  }
  return 'unknown';
};

// Initialize API URL with smart fallback
const initializeApiUrl = () => {
  const environment = detectEnvironment();
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  
  console.log(`[GC3H] Environment: ${environment}`);
  
  // PRODUCTION: Always use same-origin to prevent cross-project issues
  // This ensures deployed apps always talk to their own backend
  if (environment === 'production') {
    console.log(`[GC3H] Production mode: Using same-origin API`);
    
    // Log warning if env var is misconfigured (but don't fail)
    if (envUrl && isBlockedUrl(envUrl)) {
      console.warn(`[GC3H] WARNING: Environment has blocked URL (${envUrl}). Using same-origin instead.`);
    }
    
    return currentOrigin;
  }
  
  // STAGING: Use same-origin for preview environments
  if (environment === 'staging') {
    console.log(`[GC3H] Staging mode: Using same-origin API`);
    return currentOrigin;
  }
  
  // DEVELOPMENT: Use env var or same-origin
  if (environment === 'development') {
    if (envUrl && !isBlockedUrl(envUrl)) {
      console.log(`[GC3H] Development mode: Using env URL`);
      return envUrl;
    }
    console.log(`[GC3H] Development mode: Using same-origin`);
    return currentOrigin;
  }
  
  // UNKNOWN: Use same-origin as safest default
  console.log(`[GC3H] Unknown environment: Using same-origin`);
  return currentOrigin;
};

// The final API URL
export const API_URL = initializeApiUrl();

// Export environment for other modules
export const ENVIRONMENT = detectEnvironment();

// Health check function
export const runHealthCheck = async () => {
  if (!API_URL) {
    return { success: false, error: 'No API URL configured' };
  }
  
  try {
    const response = await fetch(`${API_URL}/api/health`, { 
      method: 'GET',
      mode: 'cors'
    });
    
    if (response.ok) {
      console.log(`[GC3H] Health check passed: ${API_URL}`);
      return { success: true, url: API_URL };
    } else {
      return { success: false, error: `Health check failed: ${response.status}` };
    }
  } catch (error) {
    return { success: false, error: `Health check error: ${error.message}` };
  }
};

// Log configuration
console.log(`[GC3H API] URL: ${API_URL}`);
